import { z } from "zod";
import { cancellationReason, identityMethodId } from "./ftthOrchestrator";
import { actions } from "./mobileOrchestrator";

const state = z.enum([
  "START",
  "ITEM_SELECTION",
  "DELIVERY",
  "IDENTITY",
  "IDENTITY_ERROR",
  "ELIGIBILITY_ERROR",
  "IDENTITY_PENDING",
  "CONSENTS",
  "CUSTOMER_VERIFICATION_PENDING",
  "CUSTOMER_VERIFICATION_ERROR",
  "AGREEMENT",
  "AGREEMENT_DATA",
  "ORDER_SUMMARY",
  "ORDER_CREATION_ERROR",
  "ORDER_CREATION_PENDING",
  "ORDER_PENDING_CANCELLATION",
  "LEGACY_MIGRATION",
  "LEGACY_MIGRATION_PENDING",
  "LEGACY_FEASIBILITY_PENDING",
  "LEGACY_MIGRATION_ERROR",
  "MNP",
  "AGREEMENT_DATA",
  "AGREEMENT",
]);

const orderSummary = z.object({
  totalOrderPrice: z.number().optional(),
  totalOrderBasePrice: z.number(),
  totalRecurringOrderPrice: z.number(),
  // totalOneTimeOrderPrice: z.number(),
  discount: z.number().optional(),
  totalOrderDiscount: z.number().optional(),
  // totalRecurringOrderBasePrice: z.number().optional(),
  items: z
    .array(
      z.object({
        offerId: z.string(),
        offerPrice: z.number(),
        offerPriceType: z.string(),
      }),
    )
    .optional(),
});

const delivery = z.object({
  country: z.string().optional(),
  street: z.string().optional(),
  streetNumber: z.string(),
  apartmentNumber: z.string().optional(),
  municipality: z.string(),
  postCode: z.string(),
  note: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  stateOrProvince: z.string().optional(),
  community: z.string().optional(),
  district: z.string().optional(),
  CBSReference: z
    .object({
      communityId: z.string().optional(),
      districtId: z.string().optional(),
      municipalityId: z.string().optional(),
      stateOrProvinceId: z.string().optional(),
      streetId: z.string().optional(),
      countryId: z.number().optional(),
    })
    .optional(),
  contactNumber: z.string().optional(),
});

const address = z.object({
  postCode: z.string(),
  municipality: z.string(),
  street: z.string().optional(),
  streetNumber: z.string(),
  apartmentNumber: z.string().optional(),
  country: z
    .object({
      code: z.string().optional(),
      name: z.string(),
    })
    .optional(),
  stateOrProvince: z.string().optional(),
  community: z.string().optional(),
  district: z.string().optional(),
  CBSReference: z
    .object({
      communityId: z.string().optional(),
      districtId: z.string().optional(),
      municipalityId: z.string().optional(),
      stateOrProvinceId: z.string().optional(),
      streetId: z.string().optional(),
      countryId: z.number().optional(),
    })
    .optional(),
});

const deliveryMethodId = z.enum([
  "COURIER",
  "ESIM",
  "BLISTER",
  "TECHNICAL_PARTNER_DELIVERY",
  "PREOWNED",
]);

const multiOrderItemProcessId = z.enum([
  "MNP_IN",
  "NEW_NUMBER",
  "LEGACY_MIGRATION",
]);

const offerCategory = z
  .union([z.literal("MOBILE"), z.literal("MOBILE_POSTPAID")])
  .optional();

const orchestratorMultiOrderItem = z.object({
  userId: z.string().optional(),
  orderId: z.string().optional(),
  bundleDocumentId: z.string().optional(),
  orderSummary: orderSummary.optional(),
  offerIds: z.array(z.string()),
  verifiedMsisdn: z.string().optional(),
  item: z.number().optional(),
  legacy: z
    .object({
      feasibilityCode: z
        .enum([
          // code descriptions: https://dop-orange.atlassian.net/browse/DOP-28419
          "RC001",
          "RC002",
          "RC003",
          "RC004",
          "RC005",
          "RC006",
          "RC007",
          "RC008",
          "RC009",
          "RC011",
          "RC012",
          "RC013",
          "RC014",
        ])
        .optional(),
      feasibilityReason: z.string().optional(),
      isMigrationFeasible: z.boolean().optional(),
      isK2Migration: z.boolean().optional(),
      blockingGroups: z.string().optional(),
      hasMasterRole: z.boolean().optional(),
    })
    .optional(),
  processId: multiOrderItemProcessId.optional(),
  deliveryMethodId: deliveryMethodId.optional(),
});

export const multiOrderOrchestrator = z.object({
  type: z.literal("NJU_MULTIORDER_ONBOARDING"),
  id: z.string(),
  userId: z.string().optional(),
  offerIds: z.array(z.string().optional()),
  offerCategory,
  cancellationReason: cancellationReason.optional(),
  state,
  actions,
  groupCapacity: z.number().optional(),
  maxGroupCapacity: z.number().optional(),
  orderId: z.string().optional(),
  applicantId: z.string().optional(),
  currentMultiOrderItem: z.number().optional(),
  multiOrderItemsCount: z.number().optional(),
  hasConsentsSet: z.boolean().optional(),
  isUserVerified: z.boolean().optional(),
  identityMethodId: identityMethodId.optional(),
  identityCardNumber: z.string().optional(),
  onfidoVerificationExceeded: z.boolean().optional(),
  verificationStatusReason: z.string().optional(),
  contactNumber: z.string().optional(),
  userContactNumber: z.string().optional(),
  delivery: delivery.optional(),
  billingAddress: address.optional(),
  correspondenceAddress: address.optional(),
  deliveryMethodId: deliveryMethodId.optional(),
  processId: z.string().optional(),
  multiOrderItems: z.array(orchestratorMultiOrderItem).optional(),
  orderSummary: orderSummary.optional(),
  orderStatus: z.string().optional(),
  isAgreementDataReadOnly: z.boolean().optional(),
  capacityDetails: z
    .array(
      z.object({
        offerCategory: offerCategory,
        capacity: z.number().optional(),
        maxCapacity: z.number().optional(),
      }),
    )
    .optional(),
  eligibilityDetails: z
    .array(
      z.object({
        offerCategory: offerCategory,
        eligible: z.boolean().optional(),
        reasonCode: z.string().optional(),
        reasonMessage: z.string().optional(),
      }),
    )
    .optional(),
});

export type IMultiOrderOrchestratorApi = z.infer<typeof multiOrderOrchestrator>;

export type IMultiOrderOrchestrator = IMultiOrderOrchestratorApi & {
  offerId?: string;
  hasNewNumberOrMnp?: boolean;
  numberOfMigratingMsisdns?: number;
};
